import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  FileUploadLabels,
  FileUploadLocalization,
  FileUploadSchemeDefinition,
  FileUploadSchemeName
} from "./constants/file-upload-types";
import { FILE_UPLOAD_SCHEME_CONFIG, FILE_UPLOAD_SCHEME_MAP } from "./constants/file-upload-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isDesktopMedium, isMobile } from "../../utils/screen-helper";
import { EonFile } from "./file-preview/constants/file-preview-types";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { FILE_UPLOAD_LOCALIZATION } from "./constants/file-upload-i18n";
import { stringFormat } from "../../utils/string-format";
import { FileUploadSchemeConfiguration } from "../../constants/colors";
import {
  FormComponent,
  handleCustomValidationDelegate,
  validateDelegate,
  validateRequiredValueDelegate
} from "../../common/form-component";
import { KeyIds } from "../../constants/keys";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

@Component({
  tag: "eon-ui-file-upload",
  styleUrl: "file-upload.scss",
  shadow: true
})
export class FileUpload
  implements
    ColorableComponent<FileUploadSchemeConfiguration<FileUploadSchemeDefinition>>,
    LocalizableComponent<FileUploadLocalization>,
    FormComponent<any, FileUploadLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: FileUploadSchemeConfiguration<FileUploadSchemeDefinition> = FILE_UPLOAD_SCHEME_CONFIG;
  schemeMap = FILE_UPLOAD_SCHEME_MAP;

  localization: Localization<FileUploadLocalization> = FILE_UPLOAD_LOCALIZATION;
  currentLocalization: FileUploadLocalization;
  fileUploadLabels: FileUploadLabels;
  requestHeaders: {};

  /**
   * Internal use only! Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) isFormComponent: boolean = true;

  /**
   * Custom required message show instead of default required message
   */
  @Prop() customRequiredMessage: string;

  /**
   * Error message for the component
   */
  @Prop() errorMessage: string;

  /**
   * custom error message
   */
  @Prop() customErrorMessage: string;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Set to true if the Input is in error state and the error message should be displayed.
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean;

  /**
   * Event fires when the file was uploaded
   */
  @Event() fileUploaded: EventEmitter<any>;

  /**
   * Event fires when the value has changed, providing the files currently selected
   */
  @Event() valueChanged: EventEmitter<Array<EonFile>>;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: FileUploadSchemeDefinition;

  @State() isMobileDevice: boolean;
  @State() isDesktop: boolean;
  @State() fileUpload: HTMLInputElement;
  @State() buttonText: String = "Browse";
  @State() fileUploadHasFile: boolean = false;
  @State() uploadInProgress: boolean;
  @State() progress: number = 0;
  @State() uploadComplete: boolean = false;
  @State() dropzoneHover: boolean;
  @State() files: Array<EonFile> = [];
  @State() showSummary: boolean = false;
  @State() uploadError: boolean = false;
  @State() fileErrorText: string;
  @State() reachedMaxUploadSize: boolean;

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  /**
   * Error to be shown when the file is the wrong format
   */
  @Prop() customFileError: string;

  /**
   * Controls if the component is required
   */
  @Prop() required: boolean;

  /**
   * Disables the Component
   */
  @Prop() disabled: boolean;

  /**
   * Controls whether the upload button is shown or nothing
   * IF NOT: Upload must be triggered with the public "upload" method. The "value" will be filled when the file-upload has files and becomes "valid"
   * IF YES: Upload can be triggered from the upload button. the "value" will be filled when the upload is completed so the file-upload will not be valid until every file is uploaded
   */
  @Prop() showUploadButton: boolean = true;

  /**
   * Controls whether the cancel upload button is shown or nothing
   */
  @Prop() showCancelUploadButton: boolean = true;

  /**
   * The value of the file-upload
   */
  @Prop({ reflect: true }) value: string;

  /**
   * The name of the file upload input
   */
  @Prop() name: string;

  /**
   * The label for the File Upload
   */
  @Prop() label: string;

  /**
   * Controls whether the files are uploaded each one by one or as an array
   */
  @Prop() singleUpload: boolean = true;

  /**
   * Controls if it is possible to select more than one files
   */
  @Prop() multipleFiles: boolean = true;

  /**
   * The URL to upload the files to
   */
  @Prop() uploadUrl: string;

  /**
   * A valid case-insensitive filename extension, starting with a period (".") character. For example: .jpg, .pdf, or .doc.
     A valid MIME type string, with no extensions.
     The string audio/* meaning "any audio file".
     The string video/* meaning "any video file".
     The string image/* meaning "any image file".
     @example "image/*,.pdf"
   */
  @Prop() accept: string;

  /**
   * The maximal file size in !megabyte!
   */
  @Prop() maxFileSize: number;

  /**
   * The maximal upload size of all files in multipleFileUpload in !megabyte!
   */
  @Prop() maxUploadSize: number;

  // private request: XMLHttpRequest;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiFileUploadElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: FileUploadSchemeName;

  /**
   * information to be shown under the component
   */
  @Prop() information: string;

  /**
   * property is a boolean value that indicates whether or not cross-site Access-Control requests should be made using
   * credentials such as cookies, authorization headers or TLS client certificates
   */
  @Prop() withCredentials: boolean = false;

  /**
   * Define the field name / key that is used for the binary value.
   * @default "SelectedFile"
   */
  @Prop() uploadFieldName: string = "SelectedFile";

  /**
   * Trigger upload of the files which resolves when all uploads are complete
   * @returns {Promise}
   */
  @Method()
  async upload(): Promise<unknown> {
    return this.uploadFiles();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Returns the currently selected files
   */
  @Method()
  async getFiles() {
    return this.files;
  }

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Method()
  async validate(): Promise<boolean> {
    return await validateDelegate(this);
  }

  /**
   * Set request headers for XMLHttpRequest. Has to be called before upload is started.
   * @param object json object that comes with a flexible amount of request headers as key value pairs
   * @example { headerNameOne: "valueOne", headerNameTwo: "valueTwo" }
   */
  @Method()
  async setRequestHeaderObject(object) {
    this.requestHeaders = object;
  }

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.onViewportSizeChange();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
    this.updateUploadLabels();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    return await validateRequiredValueDelegate(this);
  }

  @Watch("multipleFiles")
  updateUploadLabels() {
    if (this.multipleFiles) {
      this.fileUploadLabels = this.currentLocalization.multiple;
    } else {
      this.fileUploadLabels = this.currentLocalization.single;
    }
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  /**
   * Update custom-required-message
   */
  @Watch("customRequiredMessage")
  setCustomRequiredMessage() {
    this.validate();
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobileDevice = isMobile();
    this.isDesktop = isDesktopMedium();
  }

  private uploadFiles() {
    if (!this.files || this.files?.length === 0) {
      return;
    }

    return new Promise((resolve) => {
      const newFiles = this.files.map((file) => {
        const request = new XMLHttpRequest();
        let fData = new FormData();
        fData.append(this.uploadFieldName, file.file);
        file.uploadError = false; // reset in case of retry
        file.request = request;

        // Only track progress if there uploadUrl is set. Without check progress would be 100 eventhough its a e.g. 404
        this.uploadUrl &&
          request.upload.addEventListener("progress", (e) => this.handleTransferProgress(e, file), false);

        request.upload.addEventListener(
          "error",
          () => {
            this.handleError(file);
          },
          false
        );

        request.open("POST", this.uploadUrl);

        if (this.withCredentials) {
          request.withCredentials = true;
        }

        if (this.requestHeaders) {
          for (const [name, value] of Object.entries(this.requestHeaders)) {
            request.setRequestHeader(name, value.toString());
          }
        }

        // Handle complete and error states in combination with readystate and request status.
        request.onreadystatechange = () => {
          if (request.readyState === XMLHttpRequest.DONE && request.status === 200) {
            this.handleTransferComplete(file);
            if (this.uploadComplete) {
              resolve(this.files);
            }
            if (file.uploadComplete) {
              resolve(file);
            }
          }

          if (request.readyState === XMLHttpRequest.DONE && request.status !== 200) {
            this.handleError(file);
            this.handleAbort(file.id);
          }
        };

        request.send(fData);

        this.fileErrorText = "";
        return { ...file, request: request };
      });
      this.files = [...newFiles];
    });
  }

  private handleError(file: EonFile) {
    file.uploadError = true;
    file.progress = 0;
    if (!this.singleUpload) {
      this.uploadError = true;
      this.progress = 0;
    }
    this.updateFileFromArray(file);
  }

  private handleTransferProgress(event: ProgressEvent<XMLHttpRequestEventTarget>, file: EonFile) {
    file.uploadInProgress = true;
    const progress = Math.ceil((event.loaded / event.total) * 100);
    file.progress = progress;
    if (!this.singleUpload) {
      this.uploadInProgress = true;
      this.progress = progress;
    }
    this.updateFileFromArray(file);
  }

  private handleTransferComplete(file: EonFile) {
    file.uploadComplete = true;
    file.uploadInProgress = false;
    if (!this.singleUpload) {
      this.uploadComplete = true;
      this.uploadInProgress = false;
    }
    this.updateFileFromArray(file);
  }

  private browse(e?) {
    if (this.disabled) return;

    if (e) {
      e.stopPropagation();
    }
    this.fileUpload.click();
  }

  private handleDrop(e: DragEvent) {
    if (this.disabled) {
      //if the component is disabled, do nothing
      return;
    }
    let dt = e.dataTransfer;
    let files = dt.files;
    if (files) {
      this.handleFiles(files);
    }
    this.dropzoneHover = false;
    e.preventDefault();
    e.stopPropagation();
  }

  private handleDragOver(e) {
    if (this.disabled) return;

    this.dropzoneHover = true;
    e.preventDefault();
    e.stopPropagation();
  }

  private handleDragLeave(e) {
    if (this.disabled) return;

    this.dropzoneHover = false;
    e.preventDefault();
    e.stopPropagation();
  }

  private get fileError() {
    return this.customFileError ? this.customFileError : this.currentLocalization.fileError;
  }

  validateFile(file: File) {
    if (!this.accept && !this.maxFileSize) {
      //if no string or size is provided, accept everything
      return true;
    } //first set valid to false, if any accept hits, set it to true

    let formatValid = true;
    let sizeValid = true;

    if (this.accept) {
      const acceptArray = this.accept.split(",").map((element) => element.trim());
      formatValid = acceptArray.some((element) => {
        if (file.name.endsWith(element)) {
          return true;
        } else if (element.indexOf("/*") !== -1 && file.type.startsWith(element.substring(0, element.indexOf("/*")))) {
          // If the file type (image/jpg) matches the accept type (image/*) without the slash and asterisk
          return true;
        }
        return false;
      });
      if (!formatValid) {
        this.fileErrorText = this.fileError;
      }
    }

    if (this.maxFileSize && file.size > this.maxFileSize * 1024 * 1024) {
      sizeValid = false;
      this.fileErrorText = stringFormat(this.fileUploadLabels.wrongFileSize, this.maxFileSize.toString());
    }

    return sizeValid && formatValid;
  }

  private handleFiles(files: FileList) {
    this.invalid = false; //reset invalid state
    this.fileErrorText = undefined;
    let filesArray = Array.from(files);
    if (!this.multipleFiles) {
      filesArray = [filesArray[0]];
    }
    const validFiles = filesArray.filter((file) => this.validateFile(file));
    if (validFiles.length === 0) {
      this.fileUploadHasFile = false;
    }
    const eonFiles = validFiles.map((file) => {
      let eonFile: EonFile = {
        id: this.generateUniqueId(),
        file: file
      };
      eonFile.file = file;
      eonFile.id = this.generateUniqueId();
      return eonFile;
    });

    // check if MaxUploadSize was reached
    this.checkMaxUploadSize(eonFiles);

    if (!this.multipleFiles) {
      this.files = eonFiles;
    } else {
      this.files = [...this.files, ...eonFiles];
    }

    this.valueChanged.emit(this.files);

    if (!this.showUploadButton) {
      //if the upload button is not shown, the upload is happening on a form submit, so we can add the filename here as value so the component gets validated correctly
      this.value = this.files.map((file) => file.file.name).join(",");
    }

    this.fileUploadHasFile = true;
  }

  private checkMaxUploadSize(newFiles) {
    let calculatedUploadSize = 0;
    this.reachedMaxUploadSize = false;

    if (!this.maxUploadSize || this.maxUploadSize === 0) {
      return;
    }

    calculatedUploadSize += this.countFileSize(this.files);
    calculatedUploadSize += this.countFileSize(newFiles);

    this.reachedMaxUploadSize = this.maxUploadSize * 1024 * 1024 <= calculatedUploadSize;
  }

  private countFileSize(fileArray = []) {
    let fileSize = 0;
    fileArray.forEach((value) => {
      fileSize += value.file.size;
    });
    return fileSize;
  }

  private changeFileUpload() {
    if (this.fileUpload.files?.length > 0) {
      this.fileUploadHasFile = false;
      this.handleFiles(this.fileUpload.files);
    }
  }

  @Watch("files")
  checkUploadStatus() {
    const filesInProgress = this.files.filter((file) => file.uploadInProgress);
    this.uploadInProgress = filesInProgress.length > 0;

    const filesUploadComplete = this.files.filter((file) => file.uploadComplete);
    if (this.files.length > 0 && filesUploadComplete.length > 0 && filesUploadComplete.length === this.files.length) {
      this.uploadComplete = true;
      this.fileUploaded.emit(this.singleUpload ? filesUploadComplete[0].request.response : null);

      if (this.showUploadButton) {
        //if the upload button is shown, the component gets validated when the upload is finished.
        this.value = this.files.map((file) => file.file.name).join(",");
      }
    } else {
      this.uploadComplete = false;
    }
  }

  /**
   * Generates a uniqueId
   */
  private generateUniqueId() {
    let uniqueId = Math.floor(Math.random() * 100).toString();
    for (let i = 0; i < 3; i++) {
      uniqueId += Math.floor(Math.random() * 100).toString();
    }
    return Number(uniqueId);
  }

  /**
   * Removes a file from the array
   * @param fileId the id of the file to be removed
   */
  private removeFileFromArray(fileId: number) {
    const newFiles = this.files.filter((file) => file.id != fileId);
    this.files = [];
    this.files = newFiles;

    // recalculate MaxUploadSize if it is set
    this.checkMaxUploadSize([]);
  }

  private updateFileFromArray(updatedFile: EonFile) {
    const updatedFileIndex = this.files.findIndex((file) => file.id == updatedFile.id);
    const newFiles = [...this.files];
    newFiles[updatedFileIndex] = updatedFile;
    this.files = newFiles;
  }

  private handleCancelIconClick(fileId: number) {
    this.removeFileFromArray(fileId);
    this.valueChanged.emit(this.files);
    if (this.files?.length === 0) {
      //if there are no files present anymore, reset the fileupload to show it again
      this.fileUploadHasFile = false;
      this.fileUpload.value = "";
      this.value = null;
      this.showSummary = false;
      this.uploadComplete = false;
      this.progress = 0;
    }
    this.fileErrorText = "";
  }

  private handleAbort(fileId: number) {
    const abortedFileIndex = this.files.findIndex((file) => file.id == fileId);
    const abortedFile = this.files[abortedFileIndex];
    abortedFile.request.abort();
    abortedFile.progress = 0;
    abortedFile.uploadInProgress = false;
    if (this.files.filter((file) => !file.uploadInProgress)) {
      //if no upload is in progress, reset the overall progress
      this.progress = 0;
      this.uploadInProgress = false;
    }
  }

  private handleAbortAll() {
    /* if (this.singleUpload || !this.multipleFiles) {

    } else {
      this.request.abort();
    } */
    this.files.forEach((file) => {
      //if not uploaded yet, abort the request
      if (!file.uploadComplete) {
        file.request.abort();
        file.progress = 0;
        file.uploadInProgress = false;
        this.updateFileFromArray(file);
      }
    });
    this.uploadInProgress = false;
    this.progress = 0;
  }

  private handleCancelAll() {
    this.files = [];
    this.fileUpload.value = "";
    this.value = null;
    this.fileUploadHasFile = false;
    this.valueChanged.emit(this.files);
    this.fileErrorText = "";

    // recalculate MaxUploadSize if it is set
    this.checkMaxUploadSize([]);
  }

  private newUpload() {
    this.fileUpload.value = "";
    this.progress = 0;
    this.value = null;
    this.files = [];
    this.uploadComplete = false;
    this.fileUploadHasFile = false;
    this.fileErrorText = "";
  }

  @Watch("uploadComplete")
  checkShowSummary() {
    //only show the summary on multiple selection
    this.showSummary = this.uploadComplete && !this.uploadError && this.multipleFiles;
  }

  handleDropZoneKeyDown(e: KeyboardEvent) {
    if (e.key == KeyIds.SPACE || e.key == KeyIds.ENTER) {
      e.preventDefault();
      this.browse();
    }
  }

  renderFiles() {
    //if all files are uploaded in one request, don't show the progress for each file
    // const hideProgress = !this.singleUpload && this.multipleFiles ? true : false;
    return this.files.map((file) => {
      return (
        <div>
          <eon-ui-file-preview
            file={file.file}
            file-id={file.id}
            progress={file.progress}
            request={file.request}
            upload-complete={file.uploadComplete}
            upload-error={file.uploadError}
            upload-in-progress={file.uploadInProgress}
            onCancel={(e) => this.handleCancelIconClick(e.detail)}
            onAbortUpload={(e) => this.handleAbort(e.detail)}
            onRetryUpload={() => this.uploadFiles()}
          ></eon-ui-file-preview>
        </div>
      );
    });
  }
  renderButtons() {
    const uploadButton = this.showUploadButton && (
      <eon-ui-button
        class={pfx({ fileupload__upload: true })}
        rank="primary"
        text={this.fileUploadLabels.uploadLabel}
        onClick={() => this.uploadFiles()}
        fullWidthOnMobile={true}
        disabled={this.reachedMaxUploadSize}
      ></eon-ui-button>
    );
    const cancelButton = this.showCancelUploadButton && (
      <eon-ui-button
        text={this.fileUploadLabels.cancelUploadLabel}
        rank="secondary"
        onClick={() => this.handleCancelAll()}
        fullWidthOnMobile={true}
      ></eon-ui-button>
    );
    const addMoreButton = (
      <eon-ui-button
        text={this.fileUploadLabels.addMoreLabel}
        rank="secondary"
        onClick={() => this.browse()}
        fullWidthOnMobile={true}
      ></eon-ui-button>
    );
    const stopUpload = (
      <eon-ui-button
        text={this.fileUploadLabels.stopUploadLabel}
        rank="secondary"
        onClick={() => this.handleAbortAll()}
        fullWidthOnMobile={true}
      ></eon-ui-button>
    );
    const browseFiles = (
      <eon-ui-button
        text={this.fileUploadLabels.browseFileLabel}
        rank="primary"
        onClick={() => this.browse()}
        fullWidthOnMobile={true}
        disabled={this.disabled}
      ></eon-ui-button>
    );
    const newUpload = (
      <eon-ui-button
        text={this.fileUploadLabels.newUploadLabel}
        rank="primary"
        onClick={() => this.newUpload()}
        fullWidthOnMobile={true}
      ></eon-ui-button>
    );
    if (this.uploadComplete) {
      return newUpload;
    } else if (this.uploadInProgress) {
      return stopUpload;
    } else if (!this.fileUploadHasFile) {
      return browseFiles;
    } else if (this.multipleFiles) {
      return (
        <Fragment>
          {uploadButton} {addMoreButton} {cancelButton}
        </Fragment>
      );
    } else if (!this.multipleFiles) {
      return (
        <Fragment>
          {uploadButton} {cancelButton}
        </Fragment>
      );
    }
  }

  renderMultipleProgress() {
    if ((!this.uploadInProgress && !this.uploadComplete) || this.files?.length === 0) {
      return;
    }

    let currentProgressInPercent = 0;
    let uploadedFiles = 0;
    let allFiles = 0;
    allFiles = this.files.length;
    uploadedFiles = this.files.filter((file) => file.uploadComplete).length;
    const allProgress = allFiles * 100;
    const currentProgress = this.files
      .map((file) => file.progress)
      .reduce((prev, next) => {
        return prev + next;
      });
    currentProgressInPercent = Math.ceil((currentProgress / allProgress) * 100);

    return (
      this.uploadInProgress &&
      !this.uploadComplete && (
        <div class={pfx({ "fileupload__progress-multiple": true })}>
          {this.multipleFiles && (
            <Fragment>
              <div class={pfx({ "fileupload__progress-multiple__status": true })}>
                <eon-ui-text>
                  {uploadedFiles} / {allFiles} {this.fileUploadLabels.uploadedStatus}
                </eon-ui-text>
              </div>
              <eon-ui-progress-circle
                class={pfx({ "fileupload__progress-multiple__circle": true })}
                showProgressText={true}
                progress={currentProgressInPercent}
              ></eon-ui-progress-circle>
            </Fragment>
          )}
        </div>
      )
    );
  }

  renderSummary() {
    return (
      <div class={pfx({ fileupload__wrapper: true })}>
        <div
          class={pfx({ "fileupload__upload-complete": true })}
          inner-background={this.activeScheme.uploadCompleteBackground}
        >
          <div class={pfx({ "fileupload__upload-complete__status": true })}>
            <eon-ui-progress-circle
              progress={100}
              icon-color={this.activeScheme.successProgressIconColor}
              icon-name="radio_checkbox_tick"
            ></eon-ui-progress-circle>
            <eon-ui-text
              text-style="copy"
              color={this.activeScheme.successProgressTextColor}
              class={pfx({ "fileupload__upload-complete__status__text": true })}
            >
              {stringFormat(
                this.fileUploadLabels.uploadSuccessfulString,
                this.singleUpload
                  ? this.files.filter((file) => file.uploadComplete).length.toString()
                  : this.files.length.toString()
              )}
            </eon-ui-text>
            {this.files.filter((file) => file.uploadError).length > 0 && (
              <Fragment>
                <eon-ui-progress-circle
                  progress={100}
                  icon-color={this.activeScheme.errorProgressIconColor}
                  icon-name="close"
                ></eon-ui-progress-circle>
                <eon-ui-text color={this.activeScheme.errorProgressTextColor} text-style="copy">
                  {stringFormat(
                    this.fileUploadLabels.uploadSuccessfulString,
                    this.files.filter((file) => file.uploadError).length.toString()
                  )}
                </eon-ui-text>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderPreviewWrapper() {
    return (
      <div
        class={pfx({
          "fileupload__preview-wrapper": true,
          "fileupload__preview-wrapper--has-file": this.fileUploadHasFile,
          "fileupload__preview-wrapper--has-error": this.invalid
        })}
      >
        <div
          class={pfx({
            fileupload__dropzone: true,
            "fileupload__dropzone--drag-over": this.dropzoneHover
          })}
          onDrop={(e) => this.handleDrop(e)}
          onDragOver={(e) => this.handleDragOver(e)}
          onDragLeave={(e) => this.handleDragLeave(e)}
          onDragEnter={(e) => this.handleDragOver(e)}
        >
          <div class={pfx({ "fileupload__preview-wrapper__list": true })}>{this.renderFiles()}</div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div class={pfx({ fileupload: true })}>
          <eon-ui-text text-style="copy">{this.label}</eon-ui-text>
          <div class={pfx({ fileupload__container: true, "fileupload__container--has-file": this.fileUploadHasFile })}>
            <div class={pfx({ fileupload__select: true })}>
              {this.isMobileDevice && (
                <Fragment>
                  <eon-ui-icon class={pfx({ fileupload__icon: true })} name="upload" size="normal"></eon-ui-icon>
                  <eon-ui-text textStyle="copy">{this.fileUploadLabels.selectFileLabelMobile}</eon-ui-text>
                </Fragment>
              )}
              {!this.isMobileDevice && (
                <Fragment>
                  <div
                    class={pfx({
                      fileupload__dropzone: true,
                      "fileupload__dropzone--drag-over": this.dropzoneHover,
                      "fileupload__dropzone--has-error": this.invalid,
                      disabled: this.disabled
                    })}
                    onDrop={(e) => this.handleDrop(e)}
                    onDragOver={(e) => this.handleDragOver(e)}
                    onDragLeave={(e) => this.handleDragLeave(e)}
                    onDragEnter={(e) => this.handleDragOver(e)}
                    onClick={() => this.browse()}
                    onKeyDown={(event: KeyboardEvent) => this.handleDropZoneKeyDown(event)}
                    role="button"
                    tabindex="0"
                  >
                    <eon-ui-icon
                      class={pfx({ fileupload__dropzone__icon: true })}
                      name="upload"
                      size="normal"
                    ></eon-ui-icon>
                    <eon-ui-text textStyle="copy" class={pfx({ fileupload__dropzone__text: true })}>
                      {this.fileUploadLabels.selectFileLabel}
                      {this.fileUploadLabels.selectFileLabelSeparator}
                    </eon-ui-text>
                    <eon-ui-link
                      displayInline={true}
                      forceHoverStyle={true}
                      hideIcon={true}
                      text={this.fileUploadLabels.browseLinkLabel}
                      onClick={(e) => this.browse(e)}
                      disabled={this.disabled}
                      class={pfx({ fileupload__dropzone__label: true })}
                    ></eon-ui-link>
                  </div>
                </Fragment>
              )}
              <input
                name={this.name}
                type="file"
                onChange={() => this.changeFileUpload()}
                id="_file"
                multiple={this.multipleFiles}
                accept={this.accept}
                ref={(el) => (this.fileUpload = el)}
              ></input>
            </div>
            {!this.showSummary && this.renderPreviewWrapper()}
            {this.showSummary && this.renderSummary()}
            {this.information && <eon-ui-form-message type="info" message={this.information}></eon-ui-form-message>}
            {this.invalid && <eon-ui-form-message type="error" message={this.errorMessage}></eon-ui-form-message>}
            {this.fileErrorText && (
              <eon-ui-form-message type="error" message={this.fileErrorText}></eon-ui-form-message>
            )}
            {this.reachedMaxUploadSize && (
              <eon-ui-form-message
                type="error"
                message={stringFormat(this.fileUploadLabels.wrongUploadSize, this.maxUploadSize.toFixed(2))}
              ></eon-ui-form-message>
            )}
            <div class={pfx({ fileupload__footer: true })}>
              <div class={pfx({ "fileupload__button-wrapper": true })}>{this.renderButtons()}</div>
              {this.multipleFiles && this.renderMultipleProgress()}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
